import apiClient from '../lib/apiClient';
import type { CapturedPlaceholderValue, DetectedDocField, WorkflowDefinition } from '@corner/shared';

export interface UserWorkflowDTO {
  id: string;
  name: string;
  description?: string;
  parentPresetId?: string;
  icon?: string;
  status: 'draft' | 'published';
  /** V6: free-form category label (the picker derives the category list
   *  from these). Empty/undefined = uncategorized. */
  userCategory?: string;
  definition: WorkflowDefinition;
  lastRunAt: string | null;
  runCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowCategoryDTO {
  /** Category label as stored on the workflows. */
  name: string;
  /** Number of non-deleted workflows under this category for the user. */
  workflowCount: number;
}

export async function listUserWorkflows(): Promise<UserWorkflowDTO[]> {
  const { data } = await apiClient.get<{ workflows: UserWorkflowDTO[] }>('/api/workflows');
  return data.workflows ?? [];
}

export async function getUserWorkflow(id: string): Promise<UserWorkflowDTO> {
  const { data } = await apiClient.get<{ workflow: UserWorkflowDTO }>(`/api/workflows/${id}`);
  return data.workflow;
}

export async function createUserWorkflow(
  input?: { forkPresetId?: string; name?: string; description?: string; userCategory?: string; icon?: string },
): Promise<UserWorkflowDTO> {
  const { data } = await apiClient.post<{ workflow: UserWorkflowDTO }>('/api/workflows', input ?? {});
  return data.workflow;
}

export async function updateUserWorkflow(
  id: string,
  patch: {
    name?: string;
    description?: string;
    status?: 'draft' | 'published';
    userCategory?: string;
    definition?: WorkflowDefinition;
    icon?: string;
  },
): Promise<UserWorkflowDTO> {
  const { data } = await apiClient.patch<{ workflow: UserWorkflowDTO }>(`/api/workflows/${id}`, patch);
  return data.workflow;
}

/** V6: list user's workflow categories with per-category counts. */
export async function listUserCategories(): Promise<WorkflowCategoryDTO[]> {
  const { data } = await apiClient.get<{ categories: WorkflowCategoryDTO[] }>('/api/workflows/categories');
  return data.categories ?? [];
}

export async function deleteUserWorkflow(id: string): Promise<void> {
  await apiClient.delete(`/api/workflows/${id}`);
}

export async function submitWorkflowInterestRequest(input: {
  workflowId: string;
  workflowLabel: string;
  category?: string;
  message: string;
}): Promise<void> {
  await apiClient.post('/api/workflows/requests', input);
}

// ── TemplateDocument CRUD (V2 of template memory) ───────────────────

export interface TemplateDocumentDTO {
  id: string;
  workflowId: string;
  name: string;
  sourceFileId: string;
  /** Server omits this on list responses (heavy payload). Single-doc
   *  GETs include it. */
  fieldMapSnapshot: DetectedDocField[];
  confirmedAt: string;
  lastUsedAt: string | null;
  useCount: number;
  createdAt: string;
  updatedAt: string;
}

export async function listWorkflowTemplates(workflowId: string): Promise<TemplateDocumentDTO[]> {
  const { data } = await apiClient.get<{ templates: TemplateDocumentDTO[] }>(
    `/api/workflows/${workflowId}/templates`,
  );
  return data.templates ?? [];
}

export async function getWorkflowTemplate(
  workflowId: string,
  templateId: string,
): Promise<TemplateDocumentDTO> {
  const { data } = await apiClient.get<{ template: TemplateDocumentDTO }>(
    `/api/workflows/${workflowId}/templates/${templateId}`,
  );
  return data.template;
}

export async function createWorkflowTemplate(
  workflowId: string,
  input: { name: string; sourceFileId: string; fieldMapSnapshot?: DetectedDocField[] },
): Promise<TemplateDocumentDTO> {
  const { data } = await apiClient.post<{ template: TemplateDocumentDTO }>(
    `/api/workflows/${workflowId}/templates`,
    input,
  );
  return data.template;
}

export async function updateWorkflowTemplate(
  workflowId: string,
  templateId: string,
  patch: { name?: string; fieldMapSnapshot?: DetectedDocField[] },
): Promise<TemplateDocumentDTO> {
  const { data } = await apiClient.patch<{ template: TemplateDocumentDTO }>(
    `/api/workflows/${workflowId}/templates/${templateId}`,
    patch,
  );
  return data.template;
}

export async function deleteWorkflowTemplate(workflowId: string, templateId: string): Promise<void> {
  await apiClient.delete(`/api/workflows/${workflowId}/templates/${templateId}`);
}

/**
 * V4: surgical update of a single field's bbox inside the template's
 * snapshot. Used by the SaveToTemplatePrompt — concurrent corrections
 * on different fields don't clobber each other.
 */
export async function patchWorkflowTemplateField(
  workflowId: string,
  templateId: string,
  fieldId: string,
  rect: { x: number; y: number; width: number; height: number },
): Promise<TemplateDocumentDTO> {
  const { data } = await apiClient.patch<{ template: TemplateDocumentDTO }>(
    `/api/workflows/${workflowId}/templates/${templateId}/fields/${encodeURIComponent(fieldId)}`,
    { rect },
  );
  return data.template;
}

/**
 * Capture a successful "send for signature" event as a reusable workflow.
 * Called from the "Save as Workflow" pill on the assistant confirmation
 * message. The backend resolves the source PDF + recipient shape from the
 * SigningRequest itself; the frontend only needs to forward optional
 * baseline values it captured during the original fill_pdf_form result.
 */
export async function saveContractSendAsWorkflow(input: {
  signingRequestId: string;
  name?: string;
  baselineValues?: CapturedPlaceholderValue[];
  signerRole?: string;
  dispatchMessage?: string;
  // V6 picker fields (mutually exclusive in practice):
  /** When set, skip workflow create — just add a TemplateDocument to this existing workflow. */
  addToExistingWorkflowId?: string;
  /** When set on a new workflow create, populates UserWorkflow.userCategory. Ignored when addToExistingWorkflowId is set. */
  userCategory?: string;
  /** Override for the auto-generated template name. */
  templateName?: string;
  /** Custom emoji icon for the workflow. */
  icon?: string;
}): Promise<UserWorkflowDTO> {
  const attemptSave = () =>
    apiClient.post<{ workflow: UserWorkflowDTO }>(
      '/api/workflows/from-contract-send',
      input,
    );

  try {
    const { data } = await attemptSave();
    return data.workflow;
  } catch (err: unknown) {
    // Browser-level route/network churn (e.g. net::ERR_NETWORK_CHANGED)
    // is transient in production; retry once before surfacing failure.
    const axiosErr = err as { code?: string; response?: { status?: number } };
    const status = axiosErr.response?.status ?? 0;
    const isTransient =
      !status
      || status === 408
      || status === 425
      || status === 429
      || status === 500
      || status === 502
      || status === 503
      || status === 504
      || axiosErr.code === 'ERR_NETWORK';
    if (!isTransient) throw err;

    await new Promise((resolve) => setTimeout(resolve, 450));
    const { data } = await attemptSave();
    return data.workflow;
  }
}

export interface RecentRecipient {
  email: string;
  name: string;
  role: string;
  lastUsedAt: string;
  count: number;
}

export async function listRecentRecipients(limit = 20): Promise<RecentRecipient[]> {
  const { data } = await apiClient.get<{ recipients: RecentRecipient[] }>(
    '/api/signing/recent-recipients',
    { params: { limit } },
  );
  return data.recipients ?? [];
}

// ─── Contract velocity: knowledge base + recent contracts ───────────

export interface RecentContract {
  requestId: string;
  /** Master template fileId — what the run wizard should clone from. */
  sourceFileId: string;
  /** The dispatched (filled+signed) fileId. Only useful for download/view. */
  documentFileId: string;
  documentName: string;
  fileName: string;
  recipientCount: number;
  primaryRecipient: { email: string; name: string; role: string } | null;
  status: string;
  sentAt: string;
}

export async function listRecentContracts(limit = 10): Promise<RecentContract[]> {
  const { data } = await apiClient.get<{ contracts: RecentContract[] }>(
    '/api/signing/recent-contracts',
    { params: { limit } },
  );
  return data.contracts ?? [];
}

export interface ContractsKnowledgeBase {
  folders: Array<{ id: string; name: string; updatedAt: string }>;
  files: Array<{
    fileId: string;
    fileName: string;
    conversationId: string;
    conversationTitle: string;
    folderId: string;
    lastUsedAt: string;
  }>;
  workflows: Array<{
    id: string;
    name: string;
    description: string | null;
    sourceFileId: string | null;
    updatedAt: string;
  }>;
}

export async function getContractsKnowledgeBase(): Promise<ContractsKnowledgeBase> {
  const { data } = await apiClient.get<ContractsKnowledgeBase>(
    '/api/workflows/contracts-knowledge-base',
  );
  return data;
}

// ─── Recent documents (user's conversation history) ────────────────

export interface RecentDocument {
  fileId: string;
  fileName: string;
  conversationId: string;
  conversationTitle: string;
  lastUsedAt: string;
  folder: { name: string; isContracts: boolean } | null;
}

export async function listRecentDocuments(limit = 10): Promise<RecentDocument[]> {
  const { data } = await apiClient.get<{ documents: RecentDocument[] }>(
    '/api/workflows/recent-documents',
    { params: { limit } },
  );
  return data.documents ?? [];
}

/**
 * V5: Look up the TemplateDocument that matches a given fileId's
 * contentHash. Returns null when no template exists — the frontend
 * uses this in a hook where "no template" is the normal resting state.
 */
export async function getTemplateBySourceFile(fileId: string): Promise<TemplateDocumentDTO | null> {
  try {
    const { data } = await apiClient.get<{ template: TemplateDocumentDTO | null }>(
      `/api/workflows/templates/by-source/${encodeURIComponent(fileId)}`,
      // Older servers returned 404 for "no linked template". Keep the
      // global banner muted so mixed deployments remain quiet.
      { skipGlobalErrorBanner: true },
    );
    return data.template;
  } catch (err: unknown) {
    // 404 = "no template for this file" — normal state, not an error.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}
